import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { apiClient, createProjectHeaders, formatP0Error, isContextIntegrityError } from '../lib/api';
import { ArrowLeft, Sparkles } from 'lucide-react';

const LANGUAGES = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' }
];

export default function EditProject() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    site_url: '',
    language: 'fr'
  });

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  const fetchProject = async () => {
    // P0: Validate projectId
    if (!projectId) {
      toast.error('⚠️ PROJECT_ID_REQUIRED: Sélectionnez un projet');
      setLoading(false);
      return;
    }
    
    try {
      // P0: Add project headers
      const response = await apiClient.get(`/projects/${projectId}`, {
        headers: createProjectHeaders(projectId)
      });
      setFormData({
        name: response.data.name,
        site_url: response.data.site_url,
        language: response.data.language || 'fr'
      });
    } catch (error) {
      if (isContextIntegrityError(error)) {
        toast.error(formatP0Error(error));
      } else {
        toast.error('Erreur lors du chargement du projet');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // P0: Validate projectId
    if (!projectId) {
      toast.error('⚠️ PROJECT_ID_REQUIRED');
      return;
    }
    
    setSaving(true);
    try {
      // P0: Add project headers and project_id in body
      await apiClient.put(`/projects/${projectId}`, {
        ...formData,
        project_id: projectId
      }, {
        headers: createProjectHeaders(projectId)
      });
      toast.success('Projet mis à jour avec succès !');
      navigate('/dashboard');
    } catch (error) {
      if (isContextIntegrityError(error)) {
        toast.error(formatP0Error(error));
      } else {
        toast.error(error.response?.data?.detail || 'Échec de la mise à jour du projet');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
      <header className="border-b bg-white/70 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-primary" />
            <h1 className="text-xl font-heading font-bold">Modifier le Projet</h1>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-12">
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-2xl">Paramètres du projet</CardTitle>
            <CardDescription>
              Modifiez les informations de votre projet. La langue éditoriale détermine la langue de génération de contenu.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Nom du projet *</Label>
                <Input
                  id="name"
                  placeholder="Ex: Blog Entreprise, E-commerce Mode..."
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="project-name-input"
                />
                <p className="text-xs text-muted-foreground">Un nom descriptif pour identifier votre projet</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="site_url">URL du site *</Label>
                <Input
                  id="site_url"
                  type="url"
                  placeholder="https://votre-site.com"
                  value={formData.site_url}
                  onChange={(e) => setFormData({ ...formData, site_url: e.target.value })}
                  required
                  data-testid="project-url-input"
                />
                <p className="text-xs text-muted-foreground">
                  L'IA analysera ce site pour générer du contenu adapté
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="language">Langue éditoriale *</Label>
                <Select 
                  value={formData.language} 
                  onValueChange={(value) => setFormData({ ...formData, language: value })}
                >
                  <SelectTrigger id="language" data-testid="language-select">
                    <SelectValue placeholder="Sélectionner une langue" />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code} data-testid={`language-${lang.code}`}>
                        <span className="flex items-center gap-2">
                          <span>{lang.flag}</span>
                          <span>{lang.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Tous les contenus seront générés dans cette langue
                </p>
              </div>

              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <p className="text-sm font-medium mb-2">💡 Note importante :</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• La langue affecte tous les contenus futurs</li>
                  <li>• Les contenus existants ne seront pas modifiés</li>
                  <li>• L'IA adaptera automatiquement le ton à la langue choisie</li>
                </ul>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="flex-1 rounded-full"
                  data-testid="cancel-button"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1 rounded-full"
                  data-testid="save-project-button"
                >
                  {saving ? 'Sauvegarde...' : 'Enregistrer les modifications'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
